import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiDollarSign, FiHome, FiPercent } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const benefits = [
  {
    icon: <FiDollarSign className="size-6" />,
    title: "Government grants",
    description: "Up to $30,000+ in grants depending on your state and property type",
  },
  {
    icon: <FiPercent className="size-6" />,
    title: "Stamp duty savings",
    description: "Exemptions and concessions that could save you tens of thousands",
  },
  {
    icon: <FiHome className="size-6" />,
    title: "Low deposit schemes",
    description: "Buy with as little as 2-5% deposit through government guarantees",
  },
];

const schemes = [
  {
    name: "First Home Owner Grant (FHOG)",
    description:
      "A one-off grant for first home buyers purchasing or building a new home. Amounts vary by state from $10,000 to $30,000.",
    highlight: "Up to $30,000",
  },
  {
    name: "First Home Guarantee",
    description:
      "Buy with just a 5% deposit and no Lenders Mortgage Insurance. No income caps and unlimited places from 1 October 2025.",
    highlight: "5% deposit",
  },
  {
    name: "Help to Buy",
    description:
      "Government contributes up to 40% of the purchase price for new homes, or 30% for existing homes. Buy with as little as 2% deposit.",
    highlight: "2% deposit",
  },
  {
    name: "Stamp Duty Concessions",
    description:
      "Most states offer stamp duty exemptions or concessions for first home buyers. Thresholds and amounts vary by state.",
    highlight: "Save $10,000+",
  },
];

const tips = [
  {
    title: "Start saving early",
    description:
      "Even with low deposit schemes, having more savings gives you better rates and more options. Aim to save consistently, even small amounts.",
  },
  {
    title: "Check your eligibility",
    description:
      "Grant and scheme eligibility varies by state, property type, and price. We'll help you understand exactly what you qualify for.",
  },
  {
    title: "Get pre-approved first",
    description:
      "Know your budget before you start looking. Pre-approval is free and shows sellers you're a serious buyer.",
  },
  {
    title: "Factor in all costs",
    description:
      "Beyond the deposit, budget for stamp duty (if applicable), legal fees, inspections, and moving costs.",
  },
  {
    title: "Consider location carefully",
    description:
      "Think about commute times, amenities, future growth, and your lifestyle needs - not just the property price.",
  },
  {
    title: "Use a broker",
    description:
      "We compare 70+ lenders for free, handle the paperwork, and ensure you're getting the best deal and maximising your benefits.",
  },
];

export function FirstHomeBuyerPage() {
  return (
    <>
      <SEO
        title="First Home Buyer Springfield, Brisbane & Logan"
        description="First home buyer in Springfield, Brisbane, Logan or Ipswich? Access up to $30,000 in grants, stamp duty concessions and low-deposit schemes. Free broker guidance."
        url="/home-loans/first-home-buyer"
        keywords={[
          "first home buyer Springfield",
          "first home buyer Brisbane",
          "first home buyer Logan",
          "first home buyer Ipswich",
          "first home owner grant QLD",
          "first home guarantee",
          "5 percent deposit home loan",
        ]}
        jsonLd={[
          serviceSchema({
            name: "First Home Buyer Loans",
            description:
              "First home buyer mortgage broking — grants, stamp duty concessions, First Home Guarantee scheme and low-deposit options.",
            url: "/home-loans/first-home-buyer",
            serviceType: "First Home Buyer Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
            { name: "First Home Buyer", url: "/home-loans/first-home-buyer" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link
                to="/home-loans"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <FiArrowRight className="size-4 mr-2 rotate-180" />
                Home Loans
              </Link>
              <p className="section-label mb-4">First Home Buyer</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Your first home is closer than you think
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                With the right guidance, government grants, and low deposit schemes,
                buying your first home is more achievable than ever. We'll help you
                maximise your benefits and find the right loan.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Get Started
                  <FiArrowRight className="size-4" />
                </Link>
                <Link
                  to="/guides/first-home-buyer-guide"
                  className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                >
                  Read Full Guide
                </Link>
              </div>
            </div>
            <div className="grid gap-4">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-border/50"
                >
                  <div className="p-3 bg-accent/20 rounded-xl text-primary">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Schemes */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Available Schemes</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Grants & government support
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              There are multiple federal and state schemes designed to help first
              home buyers. Here are the main ones - and you may qualify for several.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {schemes.map((scheme) => (
              <div
                key={scheme.name}
                className="bg-white rounded-2xl p-6 border border-border/50 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-semibold text-lg">{scheme.name}</h3>
                  <span className="text-xs font-medium text-primary bg-accent/30 px-2 py-1 rounded-full">
                    {scheme.highlight}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">{scheme.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/guides/first-home-buyer-guide"
              className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            >
              See all grants by state
              <FiArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* How we help */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">
                How We Help
              </p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                First home buyer specialists
              </h2>
              <p className="text-primary-foreground/80 mb-8">
                Buying your first home can feel overwhelming. We simplify the process
                by guiding you through every step - from understanding your options
                to getting the keys.
              </p>
              <ul className="space-y-4">
                {[
                  "Assess your eligibility for all available grants and schemes",
                  "Compare 70+ lenders to find the best rate and features",
                  "Handle the paperwork and liaise with lenders on your behalf",
                  "Support you through to settlement and beyond",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <FiCheck className="size-5 text-primary-foreground flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 rounded-3xl p-8">
              <p className="text-2xl font-serif mb-4">
                "We saved over $25,000 using the First Home Guarantee and state grants.
                {broker.name} made the whole process easy to understand."
              </p>
              <p className="text-primary-foreground/90">
                — Recent first home buyer
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Tips</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              First home buyer tips
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tips.map((tip, index) => (
              <div
                key={tip.title}
                className="p-6 bg-muted/30 rounded-2xl"
              >
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium mb-4">
                  {index + 1}
                </div>
                <h3 className="font-semibold mb-2">{tip.title}</h3>
                <p className="text-sm text-muted-foreground">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculators */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Tools</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Helpful calculators
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/calculators/borrowing-power"
              className="group bg-white rounded-2xl p-6 border border-border/50 hover:shadow-md transition-all"
            >
              <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                Borrowing Power
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Find out how much you could borrow based on your income and expenses.
              </p>
              <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Calculate
                <FiArrowRight className="size-3" />
              </span>
            </Link>
            <Link
              to="/calculators/stamp-duty"
              className="group bg-white rounded-2xl p-6 border border-border/50 hover:shadow-md transition-all"
            >
              <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                Stamp Duty
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Estimate stamp duty and see first home buyer exemptions by state.
              </p>
              <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Calculate
                <FiArrowRight className="size-3" />
              </span>
            </Link>
            <Link
              to="/calculators/repayments"
              className="group bg-white rounded-2xl p-6 border border-border/50 hover:shadow-md transition-all"
            >
              <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                Repayments
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                See what your monthly repayments would be at different loan amounts.
              </p>
              <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Calculate
                <FiArrowRight className="size-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to buy your first home?"
        title="Let's maximise your grants and find the right loan"
        description="Book a free consultation and we'll assess your eligibility for all available schemes."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
